import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import DataTable, { Column } from '@shared/ui/data-table';
import { Order } from '../../../types/interfaces';
import { OrderActions } from '../../order-actions';
import { OrderCardExpanded } from '../../order-history-block/ui/OrderCardExpanded';
import { DateDisplay } from '../../order-history-block/ui/DateDisplay';
import { orderStatusData } from '../../order-history-block/ui/lib/orderStatusData';
import { NumberFormatter } from '../../../shared/ui/number-formatter';
import { useAppSelector } from '../../../app/store/store';
import { selectLoadingOrderId } from '../../../entities/order/model';
import spinner from '../../../shared/assets/sprites/spinner.svg';
import { getCurrencySymbol } from '@shared/lib/currency';

interface OrderListProps {
  orders: Order[];
  legalEntity: { value: number; label: string } | null;
  search: string;
}

const ORDERS_PER_PAGE = 10;

/**
 * Иконка-шеврон в последней ячейке. Поворачивается на 180° когда
 * строка раскрыта — визуальный сигнал «эта строка открыта».
 */
const Chevron = ({ open }: { open: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`transition-transform duration-200 text-gray-400 ${
      open ? 'rotate-180' : ''
    }`}
    aria-hidden
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export const OrderList = ({ orders, legalEntity, search }: OrderListProps) => {
  const [openOrderId, setOpenOrderId] = useState<string | number | null>(null);
  const loadingOrderId = useAppSelector(selectLoadingOrderId);

  // При смене фильтра/поиска свёртываем все строки — иначе можно
  // оказаться в ситуации «открытая строка скрылась фильтром, но
  // openOrderId висит и при возврате фильтра строка опять
  // распахнётся, чего юзер не ожидает».
  useEffect(() => {
    setOpenOrderId(null);
  }, [legalEntity, search]);

  const filteredOrders = useMemo(() => {
    if (!orders) return [];
    return orders.filter((order) => {
      const matchesLegalEntity =
        legalEntity && legalEntity.value
          ? order.legal_entity_data?.id === legalEntity.value
          : true;
      const matchesSearch = order.name
        .toLowerCase()
        .includes(search.toLowerCase());
      return matchesLegalEntity && matchesSearch;
    });
  }, [orders, legalEntity, search]);

  const columns: Column<Order>[] = [
    {
      key: 'name',
      header: 'Номер',
      render: (o) => (
        <div className="min-w-0">
          <p className="font-medium text-[#2d2c2c] truncate" title={o.name}>
            {o.name}
          </p>
          <p className="text-xs text-gray-400">
            от{' '}
            {moment(o.created_at).local().format('DD.MM.YYYY, HH:mm')}
          </p>
        </div>
      ),
    },
    {
      key: 'legal_entity',
      header: 'Юр. лицо',
      render: (o) => (
        <span className="text-gray-600 text-sm">
          {o.legal_entity_data?.name || '—'}
        </span>
      ),
    },
    {
      key: 'total',
      header: 'Стоимость',
      className: 'whitespace-nowrap',
      render: (o) => {
        const status = o.order_status?.status;
        // Символ валюты — из самого заказа (бэк отдаёт `currency`).
        // Без этого у казахских клиентов цены отображались в ₽,
        // потому что было захардкожено `0 ₽` / `<NumberFormatter ... /> ₽`.
        const currencySymbol = getCurrencySymbol(
          (o as any)?.currency || 'rub'
        );
        if (status === 'canceled') {
          return (
            <span className="text-gray-400 text-sm">{`0 ${currencySymbol}`}</span>
          );
        }
        const total = (o.items || []).reduce(
          (acc: number, it: any) =>
            it.cancel ? acc : acc + Number(it.price) * Number(it.quantity),
          0
        );
        return (
          <span className="text-[#2d2c2c] text-sm font-medium">
            <NumberFormatter number={total} /> {currencySymbol}
          </span>
        );
      },
    },
    {
      key: 'track',
      header: 'Трек-номер',
      render: (o) => (
        <span className="text-gray-600 text-sm">
          {o.delivery_track_number || (
            <span className="text-gray-400">Не указан</span>
          )}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Статус',
      render: (o) => {
        const detail = o.order_status?.detail || '';
        const data = orderStatusData[detail];
        if (!data) return <span className="text-gray-400 text-xs">—</span>;
        return (
          <div className="flex items-center gap-1.5 text-sm font-medium leading-tight">
            {data.icon &&
              React.createElement(data.icon, {
                color: data.color,
                size: 16,
              })}
            <span style={{ color: data.color }}>
              {data.text.split('\n').map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  <br />
                </React.Fragment>
              ))}
            </span>
          </div>
        );
      },
    },
    {
      key: 'shipment_date',
      header: 'Дата отгрузки',
      render: (o) => (
        <div className="text-sm">
          <DateDisplay
            order={o}
            canceled={o.order_status?.status === 'canceled'}
          />
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      stopRowClick: true,
      headerClassName: 'w-[1%]',
      className: 'text-right whitespace-nowrap',
      render: (o) =>
        o.order_status?.status === 'canceled' ? null : (
          <OrderActions order={o} />
        ),
    },
    {
      key: 'expand',
      header: '',
      headerClassName: 'w-[1%]',
      className: 'text-right',
      render: (o) => {
        const isLoading =
          loadingOrderId && String(loadingOrderId) === String(o.id);
        if (isLoading) {
          return (
            <img
              src={spinner}
              alt=""
              className="inline-block w-4 h-4 object-contain"
            />
          );
        }
        return <Chevron open={openOrderId === o.id} />;
      },
    },
  ];

  return (
    <DataTable<Order>
      columns={columns}
      data={filteredOrders}
      // У типа `IOrderResponse.id` стоит `?:`, на практике id есть
      // всегда (заказы без id просто не приходят с бэка). Дефолтим
      // в 0, чтобы TS не ругался на `string | number` контракт
      // keyAccessor — реальные коллизии тут невозможны.
      keyAccessor={(o) => o.id ?? 0}
      onRowClick={(o) => {
        const id = o.id;
        if (id == null) return;
        setOpenOrderId((prev) => (prev === id ? null : id));
      }}
      isExpanded={(o) =>
        openOrderId != null && o.id != null && openOrderId === o.id
      }
      renderExpanded={(o) => (
        <div className="bg-white border-t border-gray-100">
          <OrderCardExpanded order={o} />
        </div>
      )}
      pageSize={ORDERS_PER_PAGE}
      emptyMessage="Заказов не найдено"
    />
  );
};
